import Dexie, { Table } from 'dexie'

// ─── Cache ────────────────────────────────────────────────────────────────────

export interface CacheRow {
  key: string
  updatedAt: number
  payload: unknown
}

// ─── Outbox (offline mutation queue) ──────────────────────────────────────────

export type OutboxPriority = 'high' | 'normal' | 'low'

export interface OutboxRow {
  id?: number
  op: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  entity: string
  entityId: string
  key: string
  payload: unknown
  etag?: string
  /**
   * Optional explicit base URL for the entity (e.g. '/api/procurement/loadobservation').
   * When unset, the executor derives '/api/{entity.toLowerCase()}'. Use this for
   * module-prefixed ABP routes that don't match the default derivation.
   */
  baseUrl?: string
  priority: OutboxPriority
  attempts: number
  maxAttempts: number
  lastAttempt: number | null
  error: string | null
  clientCreatedAt: number
  clientModifiedAt: number
  createdAt: number
}

// ─── Per-entity sync metadata ─────────────────────────────────────────────────

export interface SyncMetaRow {
  entity: string
  lastSyncTime: string | null
  lastFullSyncTime: string | null
  rowCount: number
  strategy: 'fullSync' | 'recentPages' | 'manual'
}

// ─── Conflict resolution ──────────────────────────────────────────────────────

export type ConflictResolution = 'local' | 'server' | 'merged'

export interface ConflictRow {
  id?: number
  entity: string
  entityId: string
  localData: unknown
  serverData: unknown
  outboxId: number | null
  diffFields: string[]
  createdAt: number
  resolvedAt: number | null
  resolution: ConflictResolution | null
}

// ─── Global metadata ──────────────────────────────────────────────────────────

export interface MetaRow {
  name: string
  value: string
}

// ─── GIS layers (manual, not DSL-generated) ───────────────────────────────────

export interface GisLayerRow {
  workorderId: string
  layerCode: string
  geoJson: string          // serialised FeatureCollection
  loadedAt: string         // ISO timestamp
  sourceVersion: string | null
}

// ─── Database ─────────────────────────────────────────────────────────────────

export class OfflineDB extends Dexie {
  cache!: Table<CacheRow, string>
  outbox!: Table<OutboxRow, number>
  syncMeta!: Table<SyncMetaRow, string>
  conflicts!: Table<ConflictRow, number>
  meta!: Table<MetaRow, string>
  gisLayers!: Table<GisLayerRow, [string, string]>

  constructor() {
    super('nexo_offline_v1')

    // v1 — original schema
    this.version(1).stores({
      cache: 'key',
      outbox: '++id, entity, createdAt',
      meta: 'name'
    })

    // v2 — expanded outbox, sync metadata, conflicts
    this.version(2).stores({
      cache: 'key',
      outbox: '++id, [priority+createdAt], [entity+createdAt], entity, createdAt',
      syncMeta: 'entity',
      conflicts: '++id, entity, entityId, createdAt',
      meta: 'name'
    }).upgrade(tx => {
      // Backfill existing outbox rows with new required fields
      return tx.table('outbox').toCollection().modify(row => {
        row.entityId = row.entityId ?? row.key ?? ''
        row.priority = row.priority ?? 'normal'
        row.attempts = row.attempts ?? 0
        row.maxAttempts = row.maxAttempts ?? 5
        row.lastAttempt = row.lastAttempt ?? null
        row.error = row.error ?? null
        row.clientCreatedAt = row.clientCreatedAt ?? row.createdAt
        row.clientModifiedAt = row.clientModifiedAt ?? row.createdAt
      })
    })

    // v3 — GIS layer cache (prototype, not DSL-generated)
    this.version(3).stores({
      cache: 'key',
      outbox: '++id, [priority+createdAt], [entity+createdAt], entity, createdAt',
      syncMeta: 'entity',
      conflicts: '++id, entity, entityId, createdAt',
      meta: 'name',
      gisLayers: '[workorderId+layerCode], workorderId, layerCode'
    })
  }
}

export const offlineDB = new OfflineDB()
